import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";

const filterOptions = createFilterOptions({
  trim: true,
});

const formatTicketPrimary = ticket => {
  const contactName = ticket.contact?.name || ticket.contact?.number || `#${ticket.id}`;
  return `#${ticket.id} - ${contactName} (${ticket.status})`;
};

const formatTicketSecondary = ticket => {
  const details = [
    ticket.contact?.number || null,
    ticket.queue?.name
      ? `${i18n.t("forwardMessageModal.queue")}: ${ticket.queue.name}`
      : null,
    ticket.user?.name
      ? `${i18n.t("forwardMessageModal.assignedTo")}: ${ticket.user.name}`
      : `${i18n.t("forwardMessageModal.assignedTo")}: ${i18n.t(
          "forwardMessageModal.unassigned"
        )}`,
    ticket.whatsapp?.name
      ? `${i18n.t("forwardMessageModal.channel")}: ${ticket.whatsapp.name}`
      : null,
  ].filter(Boolean);

  return details.join(" • ");
};

const formatTicketLabel = ticket =>
  [formatTicketPrimary(ticket), formatTicketSecondary(ticket)]
    .filter(Boolean)
    .join(" ");

const ForwardMessageModal = ({
  message,
  modalOpen,
  onClose,
  currentTicketId,
}) => {
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!modalOpen) {
      setOptions([]);
      setSearchParam("");
      setSelectedTicket(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchTickets = async () => {
        try {
          const params = {
            pageNumber: 1,
            showAll: true,
          };

          if (searchParam.trim()) {
            params.searchParam = searchParam.trim();
          }

          const { data } = await api.get("/tickets", { params });

          setOptions(
            data.tickets.filter(
              ticket =>
                ticket.status !== "closed" &&
                ticket.id !== Number(currentTicketId)
            )
          );
        } catch (err) {
          toastError(err);
        } finally {
          setSearchLoading(false);
        }
      };

      fetchTickets();
    }, 350);

    return () => clearTimeout(delayDebounceFn);
  }, [currentTicketId, modalOpen, searchParam]);

  const handleClose = () => {
    setOptions([]);
    setSearchParam("");
    setSelectedTicket(null);
    onClose();
  };

  const handleForwardMessage = async e => {
    e.preventDefault();

    if (!message?.id || !selectedTicket?.id) {
      return;
    }

    setSubmitting(true);

    try {
      await api.post(`/messages/${message.id}/forward`, {
        targetTicketId: selectedTicket.id,
      });

      toast.success(i18n.t("forwardMessageModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleForwardMessage}>
        <DialogTitle>{i18n.t("forwardMessageModal.title")}</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={options}
            value={selectedTicket}
            loading={searchLoading}
            filterOptions={filterOptions}
            getOptionLabel={option => formatTicketLabel(option)}
            getOptionSelected={(option, value) => option.id === value.id}
            noOptionsText={i18n.t("forwardMessageModal.noOptions")}
            onChange={(e, newValue) => setSelectedTicket(newValue)}
            onInputChange={(e, newValue) => setSearchParam(newValue || "")}
            renderOption={option => (
              <div>
                <Typography variant="body2">{formatTicketPrimary(option)}</Typography>
                <Typography color="textSecondary" variant="caption">
                  {formatTicketSecondary(option) ||
                    i18n.t("forwardMessageModal.ticketHint")}
                </Typography>
              </div>
            )}
            renderInput={params => (
              <TextField
                {...params}
                autoFocus
                variant="outlined"
                label={i18n.t("forwardMessageModal.fieldLabel")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {searchLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <ButtonWithSpinner
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            loading={false}
            disabled={submitting}
          >
            {i18n.t("forwardMessageModal.buttons.cancel")}
          </ButtonWithSpinner>
          <ButtonWithSpinner
            variant="contained"
            type="submit"
            color="primary"
            loading={submitting}
            disabled={submitting || !selectedTicket}
          >
            {i18n.t("forwardMessageModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ForwardMessageModal;
